import {Schema} from "effect";

// ── Schema ──────────────────────────────────────────────────────────────

/**
 * An enterprise UID is a string that starts with "ent_" followed by an arbitrary suffix.
 *
 * Effect Schema equivalent of the legacy zod `EnterpriseUid` declared in
 * `@buildwithflux/identifiers`. Acceptance semantics are identical (see
 * runtime parity tests in `EnterpriseUid.test.ts`).
 */
const EnterpriseUidSchema = Schema.TemplateLiteral("ent_", Schema.String).pipe(Schema.brand("EnterpriseUid"));

export type EnterpriseUid = typeof EnterpriseUidSchema.Type;

// ── Refinement predicate ───────────────────────────────────────────────

/**
 * Type guard — decides whether a string is a branded EnterpriseUid.
 */
export const isEnterpriseUid = Schema.is(EnterpriseUidSchema);

// ── Constructors ────────────────────────────────────────────────────────

/**
 * Mint a new EnterpriseUid by generating a fresh UUID v4 suffix.
 *
 * Matches the legacy zod `createNewEnterpriseUid()` 1:1 — no-arg, internally
 * generates the UUID — so M1.3 codemods are pure symbol swaps.
 */
function createNewEnterpriseUid(): EnterpriseUid {
    return `ent_${crypto.randomUUID()}` as EnterpriseUid;
}

// ── Companion object ────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const EnterpriseUid = {
    schema: EnterpriseUidSchema,
    isEnterpriseUid,
    createNewEnterpriseUid,
} as const;
